import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { observer } from "@legendapp/state/react";
import { callWithAuth, checkVideoEmbed } from "@/api/Api";
import {
  generateActivitiesForLesson,
  priorityWordTracker,
  type GeneratableActivityType,
} from "@/activities";
import { cefrToVideoLevel } from "@/activities/cefrVideoLevel";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useChapterInfo } from "@/hooks/useChapterInfo";
import type { UserSettings } from "@shared_types";
import { getFeedLessonData } from "../api/feedApi";
import {
  lessonDataCache$,
  captionsVisible$,
  panelMode$,
  selectedWord$,
  selectedPhraseGloss$,
  handlePanelClose,
  generatedActivities$,
  maxPositionReachedMs$,
  resetMaxPositionReached,
  wordlistModalOpen$,
} from "../state/videoState";
import { dropBrokenChapter } from "../state/feedState";
import { toggleWordKnown } from "../lib/wordStatus";
import { VideoPlayer } from "./VideoPlayer";
import FeedReelSkeleton from "../components/FeedReelSkeleton";
import { Transcript } from "./Transcript";
import { ActionBar } from "../components/ui/ActionBar";
import { BottomPanel } from "../components/ui/BottomPanel";
import { Scrubber } from "../components/ui/Scrubber";
import { CenterPlayPause } from "../components/ui/CenterPlayPause";
import { LessonNavigation } from "../components/ui/LessonNavigation";
import { TopBar, TOP_BAR_HEIGHT } from "../components/ui/TopBar";
import { FeedWordlistModal } from "./FeedWordlistModal";
import { VIDEO_MAX_W, resolveVideoSource } from "./videoConstants";
import type { VideoActivity } from "../types";

/**
 * Map the user's per-type activity toggles to the generator's enabled-type list,
 * mirroring iOS `getAvailableActivitiesForFeed` (isGuessTheWordEnabled → mcq +
 * mcq-reverse, isMatchTheWordsEnabled → match, isSpellTheWordEnabled → write,
 * isFillInTheBlankEnabled → fill).
 */
function enabledActivityTypes(
  settings: UserSettings,
): GeneratableActivityType[] {
  const types: GeneratableActivityType[] = [];
  if (settings.isGuessTheWordEnabled) {
    types.push("mcq", "mcq-reverse");
  }
  if (settings.isMatchTheWordsEnabled) types.push("match");
  if (settings.isSpellTheWordEnabled) types.push("write");
  if (settings.isFillInTheBlankEnabled) types.push("fill");
  return types;
}

// Lessons whose clip-relative progress accumulator has already been armed this
// session. QuizGenerationTrigger unmounts when an activity/quiz opens and
// remounts on return — its `[lessonId]` effect would re-run and zero the
// accumulator for the SAME lesson, wiping real watch progress (making the
// leave-classifier report feed_video_skipped with ~0 watch_time and disarming
// the 90% regen). iOS keeps maxPositionReachedRef at the persistent FeedNew
// level, surviving activity round-trips; we mirror that by arming each lesson
// only the first time we see it.
const armedLessons = new Set<number>();

/**
 * Renders nothing — watches the active lesson's playback and generates the
 * activity set at iOS-parity thresholds (FeedNew.tsx:1227-1341):
 *
 *   - 55%: first generation. Snapshot the count of "newly learning" words
 *     (Priority-1) the user has marked SO FAR for this lesson.
 *   - 90%: if the user marked MORE P1 words between 55% and 90%, regenerate
 *     (forceRegenerate) so the freshly-tapped words make it into the quiz.
 *
 * Isolated in its own observer so the per-tick `videoPlayed$` updates don't
 * re-render the whole VideoLayout subtree.
 */
const QuizGenerationTrigger = observer(function QuizGenerationTrigger({
  chapterId,
  lessonId,
  activity,
}: {
  chapterId: number;
  lessonId: number;
  activity: VideoActivity;
}) {
  // iOS maxPositionReached model: progress is the furthest clip-relative
  // position reached (accumulated from linear watch-time deltas AND word-seek
  // jumps), compared against 55%/90% of the clip's own duration. This avoids
  // YouTube's whole-video played fraction, which can read negative before the
  // start-seek lands or exceed 1 (FeedNew.tsx handleWatchTimeUpdate /
  // handleWordStatusChanged).
  const maxPositionMs = maxPositionReachedMs$[lessonId].get() ?? 0;
  const lessonLenMs = Math.max(1, activity.end - activity.start);
  const lessonProgress = maxPositionMs / lessonLenMs;
  const reached55 = lessonProgress >= 0.55;
  const reached90 = lessonProgress >= 0.9;

  // User activity settings gate feed generation, exactly like iOS
  // useFeedActivityGeneration: a disabled feed-activities flag skips generation,
  // per-type toggles restrict which activities are eligible, and activityCount
  // sets how many we emit (default 3). Read into a ref so the per-second
  // playback ticks always see the latest value without re-subscribing.
  const { settings } = useUserSettings();
  const settingsRef = useRef<UserSettings | undefined>(settings);
  settingsRef.current = settings;

  // REAL per-chapter numeric video level (iOS `useChapterInfo` →
  // `chapterInfo.videoLevel`). The feed payload (ChapterFeedV2) doesn't carry
  // it, so we fetch the chapter to drive spelling gating + smart-distractor
  // difficulty off real data instead of the user's CEFR-derived fallback.
  // Read into a ref so the playback-driven generate() always sees the latest.
  const { data: chapterInfoData } = useChapterInfo(chapterId);
  const videoLevelRef = useRef<number | undefined>(chapterInfoData?.videoLevel);
  videoLevelRef.current = chapterInfoData?.videoLevel;

  // P1 (newly-learning) count snapshotted at the 55% trigger, to compare at 90%.
  const p1CountAt55 = useRef<number | null>(null);
  const has55 = useRef(false);
  const has90 = useRef(false);
  // Monotonic token so a slow in-flight pass can't overwrite a newer result —
  // e.g. a 55% generation resolving AFTER a 90% force-regen (last-writer-wins).
  const genToken = useRef(0);

  // Reset the per-lesson trigger flags whenever the lesson changes (cheap; safe
  // to re-run on remount since generated activities are cached).
  useEffect(() => {
    p1CountAt55.current = null;
    has55.current = false;
    has90.current = false;
    genToken.current = 0;
    // Arm the clip-relative progress accumulator ONCE per lesson per session.
    // Re-arming on every mount would zero progress when this trigger remounts
    // after an activity/quiz round-trip for the same lesson (see armedLessons).
    if (!armedLessons.has(lessonId)) {
      armedLessons.add(lessonId);
      resetMaxPositionReached(lessonId);
    }
  }, [lessonId]);

  const generate = useCallback(
    (force: boolean) => {
      const words = activity.userVideoWords;
      if (!words || words.length === 0) return;
      const settings = settingsRef.current;
      // Honor the feed-activities master switch (iOS short-circuits to an empty
      // activity set when areFeedActivitiesEnabled is false). Default ON to match
      // iOS settingsWithDefaults for users whose backend row predates the flag.
      if (settings && settings.areFeedActivitiesEnabled === false) {
        generatedActivities$[lessonId].set([]);
        return;
      }
      // mark in-progress so concurrent ticks don't double-fire (only on first gen)
      if (!force && generatedActivities$[lessonId].peek() !== undefined) return;
      if (!force) generatedActivities$[lessonId].set(null);

      // Thread the REAL per-chapter numeric video level (iOS reads it via
      // useChapterInfo → chapterInfo.videoLevel) so spelling gating + smart
      // distractors fire off real data. Only when that hasn't loaded yet do we
      // fall back to the user's feed CEFR level — the documented degraded path
      // in cefrVideoLevel.ts. The feed has no course, so courseDifficulty is
      // undefined — iOS feed generation passes no courseDifficulty either
      // (generateAllPersonalizedActivities omits it), so Super-Beginner is NOT
      // triggered from CEFR; feed words stay frequency-scored.
      const cefr = settings?.userFeedLevel;
      const realVideoLevel = videoLevelRef.current;
      const chapterInfo = {
        videoLevel: realVideoLevel ?? cefrToVideoLevel(cefr),
        // Only a real chapter level enables LLM distractors; the CEFR fallback
        // above is for spelling gating only (iOS parity).
        hasRealVideoLevel: realVideoLevel != null && realVideoLevel > 0,
        courseDifficulty: undefined,
      };
      // Per-type toggles + activity count (iOS getAvailableActivitiesForFeed /
      // currentSettings.activityCount). When settings haven't loaded yet, leave
      // both unset so the generator's all-types/default-count behavior applies.
      const enabledTypes = settings ? enabledActivityTypes(settings) : undefined;
      const maxActivities = settings?.activityCount || 3;

      const token = ++genToken.current;
      generateActivitiesForLesson(words, chapterInfo, {
        maxActivities,
        ...(enabledTypes && { enabledTypes }),
      })
        .then((acts) => {
          // Drop a stale result if a newer generation has since started (e.g.
          // a slow 55% pass landing after the 90% force-regen).
          if (token !== genToken.current) return;
          // A null/empty regen must not wipe a usable first-pass set.
          if (force && (!acts || acts.length === 0)) return;
          generatedActivities$[lessonId].set(acts);
        })
        .catch(() => {
          if (token !== genToken.current) return;
          if (!force) generatedActivities$[lessonId].set(null);
        });
    },
    [activity, lessonId],
  );

  // --- 55% first pass ---
  useEffect(() => {
    if (!reached55 || has55.current) return;
    // undefined = not yet attempted; null/object = already handled
    if (generatedActivities$[lessonId]?.peek() !== undefined) return;
    has55.current = true;
    p1CountAt55.current = priorityWordTracker.getNewlyLearningWords(lessonId).size;
    generate(false);
  }, [reached55, lessonId, generate]);

  // --- 90% conditional regen ---
  useEffect(() => {
    if (!reached90 || has90.current || !has55.current) return;
    has90.current = true;
    const currentP1 = priorityWordTracker.getNewlyLearningWords(lessonId).size;
    if (p1CountAt55.current !== null && currentP1 > p1CountAt55.current) {
      generate(true);
    }
  }, [reached90, lessonId, generate]);

  return null;
});

const VIDEO_BOTTOM_OVERLAY_PCT = 25;

// ~2–3 visible caption rows (iOS parity) — see VideoPage / PWA_BUG_FIXES #4.
const TRANSCRIPT_EXPANDED_HEIGHT = "95px";

/** Delay (ms) before collapsing transcript height, so clip-path starts first. */
const TRANSCRIPT_COLLAPSE_DELAY_MS = 90;

const transcriptCollapseStyle = (isVisible: boolean): CSSProperties => ({
  height: isVisible ? TRANSCRIPT_EXPANDED_HEIGHT : "0px",
  flex: "none",
  transitionDelay: isVisible ? "0ms" : `${TRANSCRIPT_COLLAPSE_DELAY_MS}ms`,
});

const transcriptRevealStyle = (isVisible: boolean): CSSProperties => ({
  opacity: isVisible ? 1 : 0,
  clipPath: isVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
});

interface VideoLayoutProps {
  chapterId: number;
  lessonId: number;
  isActive: boolean;
  onToggleLike: () => void;
}

const VideoLayout = observer(function VideoLayout({
  chapterId,
  lessonId,
  isActive,
  onToggleLike,
}: VideoLayoutProps) {
  // VideoLayout instances are reused across lesson/chapter changes (stable
  // carousel keys), so the error latch is keyed by lessonId — a failure on one
  // lesson must not poison later lessons (iOS retries per-lesson).
  const [errorLessonId, setErrorLessonId] = useState<number | null>(null);
  const loadError = errorLessonId === lessonId;

  // Check cache first, then fetch if active and missing
  const cachedData = lessonDataCache$[lessonId]?.get();
  const cachedActivity = cachedData?.video as VideoActivity | undefined;

  // Prefetch lesson data for EVERY mounted panel (current + the adjacent
  // prev/next that the carousels keep mounted), not just the active one — this
  // mirrors how iOS keeps ±1 lessons warm so scrolling doesn't hit the ~8s wait.
  useEffect(() => {
    if (cachedData || loadError) return;

    callWithAuth(getFeedLessonData, { lessonId })
      .then((data) => {
        if (data) {
          lessonDataCache$[lessonId].set(data);
        }
      })
      .catch(() => setErrorLessonId(lessonId));
  }, [lessonId, cachedData, loadError]);

  const isCaptionsVisible = captionsVisible$.get();

  // The backend `aspectRatio` string is unreliable (portrait clips mislabeled
  // "16:9"), so prefer the REAL decoded aspect once the player reports it (iOS
  // presentationSize parity). Reset per lesson via a render-time guard (no
  // effect) since this VideoLayout instance is reused across clips.
  const [realAspectW, setRealAspectW] = useState<number | null>(null);
  // Track the resolved player kind: YouTube draws its OWN centre play button, so
  // we hide ours for YouTube to avoid two overlapping glyphs. Only the bare
  // Cloudflare <video> (no native controls) needs our CenterPlayPause.
  const [activeSource, setActiveSource] = useState<
    "youtube" | "cloudflare" | null
  >(null);
  const aspectLessonRef = useRef<number | null>(null);
  if (aspectLessonRef.current !== lessonId) {
    aspectLessonRef.current = lessonId;
    if (realAspectW !== null) setRealAspectW(null);
    // Reset the resolved source too (this instance is reused across clips);
    // otherwise CenterPlayPause can show/hide off the previous clip's source.
    if (activeSource !== null) setActiveSource(null);
  }
  // Track whether the YouTube embed itself errored (exhausted CF fallback);
  // when it does we drop the chapter and let the feed advance (iOS
  // handleVideoError fires per error, no latch). Keyed by chapterId because
  // this instance is reused across chapters — a second broken chapter must
  // also be dropped.
  const handledBrokenRef = useRef<number | null>(null);
  const handleVideoBroken = useCallback(() => {
    if (handledBrokenRef.current === chapterId) return;
    handledBrokenRef.current = chapterId;
    const ytId = cachedActivity?.youtubeId;
    if (ytId) {
      // Fire-and-forget embed check (telemetry / future allow-listing on the
      // backend); iOS calls this on every player error.
      callWithAuth(checkVideoEmbed, { youtube_id: ytId }).catch(() => {
        /* ignore — we drop the chapter regardless */
      });
    }
    dropBrokenChapter(chapterId);
  }, [cachedActivity, chapterId]);
  const panelMode = isActive ? panelMode$.get() : null;
  const wordForPanel = isActive ? selectedWord$.get() : null;
  const phraseGloss = isActive && panelMode === "sentence" ? selectedPhraseGloss$.get() : undefined;

  /** Toggle the selected word's "known" status (checkmark in the word panel). */
  const handleToggleWordKnown = useCallback(() => {
    const word = selectedWord$.peek();
    if (!word) return;
    // Canonical toggle shared with the transcript double-tap gesture.
    toggleWordKnown(word, lessonId);
  }, [lessonId]);

  return (
    <div
      className={`h-full w-full flex flex-col items-center bg-black${!isActive ? " pointer-events-none" : ""}`}
    >
      {isActive && (
        <div className="fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
          {/* Phone-width top bar even on wide desktops (the listening bar should
              look the same as on a phone, not stretch across the screen). */}
          <div className="w-full max-w-[420px] pointer-events-auto">
            <TopBar />
          </div>
        </div>
      )}

      <div className="shrink-0" style={{ height: TOP_BAR_HEIGHT }} />

      <div className="w-full flex-1 min-h-0 flex justify-center">
        {/* Column keeps a real (flex) width at all breakpoints — an auto-width
            (shrink-to-content) column can't resolve a width from the video
            stage's percentage-sized children and collapses to the iframe's tiny
            intrinsic size. */}
        <div className="flex flex-col flex-1 min-h-0 px-0 py-3 md:py-4">
          {/* Video stage (iOS FeedNew overlay mode). */}
          {(() => {
            // iOS feed: WIDE (landscape) clips letterbox with the transcript in a
            // strip BELOW the video; VERTICAL clips fill the frame with the
            // transcript OVERLAID. Prefer the REAL decoded aspect once the player
            // reports it (Cloudflare <video>). Before that, use the source the
            // clip WILL play from (resolved synchronously, so there's no null
            // window that flashes the wrong column):
            //  - YouTube can NEVER report dimensions (iframe), so trust the
            //    metadata there (portrait only when explicitly "9:16").
            //  - Cloudflare WILL report soon, and the metadata mislabels portrait
            //    clips as "16:9", so default to the portrait column to avoid the
            //    clip flashing wide then snapping narrow.
            const resolvedSource = activeSource ?? resolveVideoSource(cachedActivity);
            const isVertical =
              realAspectW != null
                ? realAspectW < 1
                : resolvedSource === "youtube"
                  ? cachedActivity?.aspectRatio === "9:16"
                  : true;

            const media = cachedActivity ? (
              isActive ? (
                <>
                  <VideoPlayer
                    videoActivity={cachedActivity}
                    lessonId={lessonId}
                    chapterId={chapterId}
                    onUnrecoverableError={handleVideoBroken}
                    onVideoAspect={setRealAspectW}
                    onSourceResolved={(s) => {
                      setActiveSource(s);
                      // YouTube has no decoded <video>, so a decoded Cloudflare
                      // aspect from BEFORE a CF→YouTube fallback must not keep
                      // driving layout — drop it and fall back to metadata.
                      if (s === "youtube") setRealAspectW(null);
                    }}
                  />
                  <QuizGenerationTrigger chapterId={chapterId} lessonId={lessonId} activity={cachedActivity} />
                </>
              ) : (
                // Inactive neighbour reel — reel skeleton (iOS FeedVideoLoadingScreen).
                <FeedReelSkeleton />
              )
            ) : loadError ? (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-white/50 text-sm">Video unavailable</p>
              </div>
            ) : (
              <FeedReelSkeleton />
            );

            // On-video chrome (action bar, word panel, scrubber) — anchored to
            // the bottom of whatever frame holds it (the band for horizontal, the
            // full-bleed video for vertical).
            const controls = (
              <>
                {isActive && activeSource === "cloudflare" && <CenterPlayPause />}
                {/* Lesson dots overlaid on the video bottom, UNDER the transcript
                    (iOS has none; we keep them for web but float them ON the frame
                    so they don't need a strip below — that lets the video grow). */}
                {isActive && (
                  <div className="absolute bottom-1 left-0 right-0 z-20 flex justify-center pointer-events-none">
                    <div className="pointer-events-auto w-[280px]">
                      <LessonNavigation />
                    </div>
                  </div>
                )}
                {/* Right rail — raised up the frame (TikTok/Reels style) on the
                    full-bleed portrait layout; pinned to the band bottom on the
                    letterboxed landscape layout. */}
                <div
                  className={`absolute right-0 z-20 pr-2 ${
                    isVertical ? "bottom-[56px]" : "bottom-0 pb-2"
                  }`}
                >
                  <ActionBar chapterId={chapterId} onToggleLike={onToggleLike} />
                </div>
                {isActive && panelMode && (
                  <div
                    className="absolute bottom-0 left-0 right-0 z-20 flex items-end pointer-events-none"
                    style={{ height: `${VIDEO_BOTTOM_OVERLAY_PCT}%` }}
                  >
                    <div
                      className="w-full pb-4 pointer-events-auto"
                      style={{ maxWidth: "calc(100% - 56px)" }}
                    >
                      <BottomPanel
                        mode={panelMode}
                        word={wordForPanel}
                        translation={phraseGloss}
                        lessonId={lessonId}
                        chapterId={chapterId}
                        onSave={handleToggleWordKnown}
                        onClose={handlePanelClose}
                      />
                    </div>
                  </div>
                )}
                {isActive && <Scrubber overlay />}
              </>
            );

            const transcriptInner = isActive ? (
              <Transcript lessonId={lessonId} overlayMode={isVertical} />
            ) : (
              cachedActivity && (
                <Transcript
                  words={cachedActivity.userVideoWords}
                  videoId={cachedActivity.youtubeId}
                  lessonId={lessonId}
                  isInteractive={false}
                  overlayMode={isVertical}
                />
              )
            );

            // VERTICAL → 9:16 portrait column (fills the screen), transcript
            // OVERLAID. LANDSCAPE → 16:9 letterboxed band, transcript BELOW. The
            // video frame is ONE element whose className just toggles, so when the
            // real aspect arrives mid-load the player resizes in place instead of
            // remounting (which would restart the video).
            return (
              <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center">
                <div
                  className={
                    isVertical
                      ? "relative mx-auto h-full max-h-full aspect-[9/16] max-w-full overflow-hidden bg-black shrink-0"
                      : `relative w-full mx-auto ${VIDEO_MAX_W} max-h-full aspect-[16/9] overflow-hidden bg-black shrink-0`
                  }
                >
                  {media}
                  {isVertical && cachedActivity && (
                    <div
                      className="absolute left-0 z-10 overflow-hidden transition-[height] duration-300 ease-in-out"
                      style={{ ...transcriptCollapseStyle(isCaptionsVisible), bottom: 84, right: 56 }}
                    >
                      <div
                        className="h-full overflow-hidden transition-[opacity,clip-path] duration-300 ease-in-out"
                        style={transcriptRevealStyle(isCaptionsVisible)}
                      >
                        {transcriptInner}
                      </div>
                    </div>
                  )}
                  {controls}
                </div>
                {!isVertical && cachedActivity && (
                  <div
                    className={`relative w-full mx-auto ${VIDEO_MAX_W} overflow-hidden transition-[height] duration-300 ease-in-out shrink-0`}
                    style={transcriptCollapseStyle(isCaptionsVisible)}
                  >
                    <div
                      className="h-full overflow-hidden transition-[opacity,clip-path] duration-300 ease-in-out"
                      style={transcriptRevealStyle(isCaptionsVisible)}
                    >
                      {transcriptInner}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Bottom spacer — clears the fixed NavBar (~72px). The lesson dots used to
          live here in a tall strip; they now overlay the video (see controls), so
          this is just the NavBar gap and the video frame above grows to fill it. */}
      <div className="w-full shrink-0 pb-20" />

      {/* Wordlist bulk-mark modal (flag-gated trigger in the ActionBar). */}
      {isActive && wordlistModalOpen$.get() && cachedActivity && (
        <FeedWordlistModal
          lessonId={lessonId}
          words={cachedActivity.userVideoWords}
          onClose={() => wordlistModalOpen$.set(false)}
        />
      )}
    </div>
  );
});

export { VideoLayout };
